import { Chord, Interval, Note } from 'tonal';
import { chromaOf } from './fretboard';
import type { ChordQuality } from './harmony';

/**
 * Chord construction and spelling (topic C5): triads, then the four common
 * sevenths, then sus and add chords, all spelled by tonal. The kinds and their
 * formulas live here so drills (D7 spelling, D8 chord tones) and skills share
 * one vocabulary. `harmony.ts` keeps its own `ChordQuality` for diatonic work;
 * `fromHarmonyQuality` maps between the two.
 */

export type ChordKind =
  | 'major'
  | 'minor'
  | 'diminished'
  | 'augmented'
  | 'maj7'
  | '7'
  | 'm7'
  | 'm7b5'
  | 'dim7'
  | 'sus2'
  | 'sus4'
  | 'add9';

/** Teaching order within C5: triads → sevenths → sus/add. */
export type ChordFamily = 'triad' | 'seventh' | 'suspended' | 'added';

/** A formula degree, relative to the chord root. */
export type ChordDegree =
  '1' | '2' | '3' | 'b3' | '4' | '5' | 'b5' | '#5' | '7' | 'b7' | 'bb7' | '9';

export interface ChordKindInfo {
  readonly kind: ChordKind;
  readonly family: ChordFamily;
  /** Symbol suffix as charts write it: `` (C), `m7b5` (Bm7b5). */
  readonly suffix: string;
  /** Spoken name, e.g. `half-diminished seventh`. */
  readonly name: string;
  /** Formula, root first: 1 3 5 b7. */
  readonly formula: readonly ChordDegree[];
  /** tonal chord type used for spelling. */
  readonly tonalType: string;
}

export const CHORD_KINDS: readonly ChordKindInfo[] = [
  {
    kind: 'major',
    family: 'triad',
    suffix: '',
    name: 'major',
    formula: ['1', '3', '5'],
    tonalType: 'M',
  },
  {
    kind: 'minor',
    family: 'triad',
    suffix: 'm',
    name: 'minor',
    formula: ['1', 'b3', '5'],
    tonalType: 'm',
  },
  {
    kind: 'diminished',
    family: 'triad',
    suffix: 'dim',
    name: 'diminished',
    formula: ['1', 'b3', 'b5'],
    tonalType: 'dim',
  },
  {
    kind: 'augmented',
    family: 'triad',
    suffix: 'aug',
    name: 'augmented',
    formula: ['1', '3', '#5'],
    tonalType: 'aug',
  },
  {
    kind: 'maj7',
    family: 'seventh',
    suffix: 'maj7',
    name: 'major seventh',
    formula: ['1', '3', '5', '7'],
    tonalType: 'maj7',
  },
  {
    kind: '7',
    family: 'seventh',
    suffix: '7',
    name: 'dominant seventh',
    formula: ['1', '3', '5', 'b7'],
    tonalType: '7',
  },
  {
    kind: 'm7',
    family: 'seventh',
    suffix: 'm7',
    name: 'minor seventh',
    formula: ['1', 'b3', '5', 'b7'],
    tonalType: 'm7',
  },
  {
    kind: 'm7b5',
    family: 'seventh',
    suffix: 'm7b5',
    name: 'half-diminished seventh',
    formula: ['1', 'b3', 'b5', 'b7'],
    tonalType: 'm7b5',
  },
  {
    kind: 'dim7',
    family: 'seventh',
    suffix: 'dim7',
    name: 'diminished seventh',
    formula: ['1', 'b3', 'b5', 'bb7'],
    tonalType: 'dim7',
  },
  {
    kind: 'sus2',
    family: 'suspended',
    suffix: 'sus2',
    name: 'suspended second',
    formula: ['1', '2', '5'],
    tonalType: 'sus2',
  },
  {
    kind: 'sus4',
    family: 'suspended',
    suffix: 'sus4',
    name: 'suspended fourth',
    formula: ['1', '4', '5'],
    tonalType: 'sus4',
  },
  {
    kind: 'add9',
    family: 'added',
    suffix: 'add9',
    name: 'added ninth',
    formula: ['1', '3', '5', '9'],
    tonalType: 'add9',
  },
];

/** The kinds C5 names explicitly: major/minor triads, maj7/7/m7/m7b5, sus2/sus4, add9. */
                                                     
          
          
         
      
       
         
         
         
         
  

/**
 * Pairs learners mix up (research/03 C5): the b7 of `7` vs the 7 of `maj7`,
 * `m7` vs `m7b5`, and the two sus chords.
 */
                                                                                     
                
                 
              
                   
                     
                   
  

                                                             

                                                      
            
            
            
           
            
            
           
             
            
           
            
            
  

/** Extra suffixes accepted by `parseChord`, beyond each kind's own. */
                                                   
             
               
               
               
                    
                  
                   
             
            
             
            
             
             
            
             
                 
               
             
              
               
  

                                                               
                                 
                                                                      
              
 

                                                                
                                         
 

                                       
                           
                                                                                     
              
 

/** Chord symbol: `nameChord('B', 'm7')` → `Bm7`. */
                                                                  
                                                        
 

export interface ParsedChord {
  readonly root: string;
  readonly kind: ChordKind;
}

/** `Bm7`, `F#m7b5`, `Bø`, `Csus`, `Eadd9`, `GΔ7` → root and kind. Throws on anything else. */
                                                         
                                                                  
                                           
                                                                                            
                                                                                   
                                                
 

/**
 * The chord's notes as pitch classes in formula order, spelled by tonal:
 * Bm7 → B D F# A; Gbdim → Gb Bbb Dbb (strict spelling keeps the letters).
 */
                                                                     
                                                                            
                     
 

/** `spellChord` from a symbol: `chordNotes('Bm7')` → B D F# A. */
                                                      
                                            
                                
 

                                                                       
                                     
 

/**
 * The note on one formula degree, or `null` if the chord doesn't have it:
 * the 3rd of D7 is F#; Csus4 has no 3.
 */
                                                                                                
                                               
                                                              
 

/** The formula degree a pitch plays in the chord, or `null`: F# in D7 → `3`. */
                                                                                                
                           
                                                                       
                                                               
 

                                                                   
                                                     
 

export interface ChordDifference {
  /** Degrees only the first kind has. */
  readonly onlyA: readonly ChordDegree[];
  /** Degrees only the second kind has. */
  readonly onlyB: readonly ChordDegree[];
}

/**
 * How two kinds differ, for feedback: `7` vs `maj7` → onlyA [b7], onlyB [7].
 */
                                                                              
                             
                             
                                                                                                
 

/** True when the notes (any order, any spelling) are exactly the chord's pitches. */
                                                                                                  
                                                             
                                           
                                                                      
 

/** True when the spelling avoids double sharps and flats (Gbdim fails; F#dim passes). */
                                                                        
                                              
 

                                                       
                 
                 
                           
                         
                 
                 
               
                             
                      
  

/** Map `harmony.ts`'s diatonic qualities onto chord kinds. */
                                                                      
                               
 
